import React, { useRef, useState } from 'react';
import Image from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

// Article graphics that can be resized by dragging a corner or typing a width/height.
// Width/height are stored as attributes so the public page renders at the chosen size.
export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute('width') || el.style.width || null,
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width, style: `width:${attrs.width}${/^\d+$/.test(attrs.width) ? 'px' : ''};` } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute('height') || el.style.height || null,
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {}),
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

const px = (v) => (v == null || v === '' ? '' : String(v).replace(/px$/, ''));

function ResizableImageView({ node, updateAttributes, selected, editor }) {
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(null); // live {w,h} while dragging
  const { src, alt, width, height } = node.attrs;
  const editable = editor.isEditable;

  const startDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    const el = imgRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = rect.width / Math.max(rect.height, 1);
    const sx = e.clientX, sw = rect.width;
    const move = (ev) => {
      const w = Math.max(40, Math.round(sw + ev.clientX - sx));
      setDrag({ w, h: Math.round(w / ratio) });
    };
    const up = (ev) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const w = Math.max(40, Math.round(sw + ev.clientX - sx));
      setDrag(null);
      updateAttributes({ width: `${w}`, height: `${Math.round(w / ratio)}` });
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  };

  const setW = (v) => {
    const w = px(v);
    if (!w) { updateAttributes({ width: null, height: null }); return; }
    const el = imgRef.current;
    const ratio = el ? el.getBoundingClientRect().width / Math.max(el.getBoundingClientRect().height, 1) : null;
    updateAttributes({ width: w, height: ratio && /^\d+$/.test(w) ? `${Math.round(Number(w) / ratio)}` : null });
  };
  const setH = (v) => {
    const h = px(v);
    if (!h) { updateAttributes({ height: null }); return; }
    const el = imgRef.current;
    const ratio = el ? el.getBoundingClientRect().width / Math.max(el.getBoundingClientRect().height, 1) : null;
    updateAttributes({ height: h, width: ratio && /^\d+$/.test(h) ? `${Math.round(Number(h) * ratio)}` : node.attrs.width });
  };

  const style = {
    width: drag ? drag.w : (width ? (/^\d+$/.test(width) ? `${width}px` : width) : undefined),
    height: drag ? drag.h : (height ? (/^\d+$/.test(height) ? `${height}px` : height) : undefined),
    maxWidth: '100%',
    display: 'block',
    outline: selected && editable ? '2px solid var(--navy)' : undefined,
    outlineOffset: 2,
  };

  return (
    <NodeViewWrapper className="rte-image" data-drag-handle style={{ display: 'inline-block', position: 'relative', maxWidth: '100%', lineHeight: 0 }} data-testid="rte-image-node">
      <img ref={imgRef} src={src} alt={alt || ''} style={style} draggable={false} />
      {editable && selected && (
        <>
          <span
            role="button"
            title="Drag to resize"
            data-testid="rte-image-resize-handle"
            onPointerDown={startDrag}
            style={{ position: 'absolute', right: -6, bottom: -6, width: 14, height: 14, borderRadius: 3, background: 'var(--gold)', border: '1px solid rgba(0,0,0,0.35)', cursor: 'nwse-resize', touchAction: 'none' }}
          />
          <div
            contentEditable={false}
            className="flex items-center gap-1 text-[11px] font-title shadow"
            style={{ position: 'absolute', left: 0, top: -30, background: 'var(--navy)', color: 'var(--cream)', padding: '3px 6px', borderRadius: 4, lineHeight: 1.2, whiteSpace: 'nowrap' }}
            data-testid="rte-image-size-bar"
          >
            W <input value={drag ? drag.w : px(width)} onChange={(e) => setW(e.target.value)} placeholder="auto" className="w-14 px-1 rounded text-[11px]" style={{ color: '#222' }} data-testid="rte-image-width" />
            H <input value={drag ? drag.h : px(height)} onChange={(e) => setH(e.target.value)} placeholder="auto" className="w-14 px-1 rounded text-[11px]" style={{ color: '#222' }} data-testid="rte-image-height" />
            px
            <button type="button" onClick={() => updateAttributes({ width: null, height: null })} className="ml-1 underline" data-testid="rte-image-size-reset">reset</button>
          </div>
          {drag && <span style={{ position: 'absolute', right: 4, bottom: 10, fontSize: 10, background: 'rgba(0,0,0,0.75)', color: 'white', padding: '1px 4px', borderRadius: 3, lineHeight: 1.4 }} data-testid="rte-image-readout">{drag.w}×{drag.h}</span>}
        </>
      )}
    </NodeViewWrapper>
  );
}
